import { formatCivilDate, formatPeriod } from '../dates/civil-date';
import { formatCurrency } from '../money/format-currency';

/**
 * Rendering a Data Suite cell (SDD-024 F-1…F-4) — in the console only; exports write raw values.
 *
 * The warehouse answers every cell as text (Athena returns varchar for everything; the Postgres
 * provider is converted to the same form, §7 step 7), so one renderer serves both providers: the
 * column's catalog TYPE says how to read the text, the report's per-field `format` (F-2) overrides
 * the workspace defaults (F-1), and a null shows the workspace's "empty" mark.
 *
 * DATE is civil (`2026-08-15`, a timestamp's date part); DATETIME is a naive UTC timestamp shown in
 * the workspace time zone (F-4). A bucketed axis (P-2) shows its period (`Juni 2026`, `Q2 2026`).
 */

export type ReportFieldType = 'STRING' | 'NUMBER' | 'DATE' | 'DATETIME' | 'BOOLEAN';
export type ReportBucket = 'YEAR' | 'QUARTER' | 'MONTH' | 'DAY';

/** A report's per-field override (`report.schema.json#/$defs/format`). */
export interface ReportFormat {
  readonly kind: 'DATE' | 'DATETIME' | 'NUMBER' | 'CURRENCY' | 'PERCENT' | 'BOOLEAN' | 'TEXT';
  readonly label?: string;
  readonly pattern?: string;
  readonly decimals?: number;
  readonly grouping?: boolean;
  readonly currency?: string;
  readonly currencyField?: string;
  readonly trueLabel?: string;
  readonly falseLabel?: string;
  readonly empty?: string;
}

/** The workspace defaults (`workspace_settings.schema.json`). */
export interface WorkspaceFormats {
  readonly locale?: string;
  readonly timeZone?: string;
  readonly formats: {
    readonly date: { readonly pattern: string };
    readonly datetime: { readonly pattern: string };
    readonly number: { readonly decimals: number; readonly grouping: boolean };
    readonly currency: { readonly code: string; readonly decimals: number };
    readonly percent: { readonly decimals: number };
    readonly boolean: { readonly trueLabel: string; readonly falseLabel: string };
    readonly empty: string;
  };
}

/** The defaults a tenant that never saved settings reads (`workspace_settings.tenant-a.json`). */
export const DEFAULT_WORKSPACE_FORMATS: WorkspaceFormats = {
  locale: 'de-DE',
  timeZone: 'Europe/Berlin',
  formats: {
    date: { pattern: 'DD.MM.YYYY' },
    datetime: { pattern: 'DD.MM.YYYY HH:mm' },
    number: { decimals: 2, grouping: true },
    currency: { code: 'EUR', decimals: 2 },
    percent: { decimals: 1 },
    boolean: { trueLabel: 'Ja', falseLabel: 'Nein' },
    empty: '—',
  },
};

export interface CellContext {
  readonly type: ReportFieldType;
  readonly format?: ReportFormat;
  readonly settings?: WorkspaceFormats;
  /** The row's currency, when the format names a `currencyField` the result projected. */
  readonly rowCurrency?: string | null;
  readonly bucket?: ReportBucket;
  /** Overrides the workspace locale (the UI language's formatting locale). */
  readonly locale?: string;
}

/** `2026-08-15 00:00:00` / `2026-08-15T00:00:00Z` / `2026-08-15` → `2026-08-15`. */
export function civilPart(raw: string): string {
  return raw.slice(0, 10);
}

function numberFormat(locale: string, decimals: number, grouping: boolean): Intl.NumberFormat {
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
    useGrouping: grouping,
  });
}

function datePattern(civil: string, pattern: string, locale: string): string {
  const [y, m, d] = civil.split('-');
  if (y === undefined || m === undefined || d === undefined) return civil;
  switch (pattern) {
    case 'YYYY-MM-DD':
      return civil;
    case 'DD/MM/YYYY':
      return `${d}/${m}/${y}`;
    case 'DD.MM.YYYY':
      return `${d}.${m}.${y}`;
    default:
      return formatCivilDate(civil, locale);
  }
}

function bucketLabel(civil: string, bucket: ReportBucket, locale: string, pattern: string): string {
  const [year, month] = civil.split('-');
  switch (bucket) {
    case 'YEAR':
      return year ?? civil;
    case 'QUARTER':
      return `Q${String(Math.floor((Number(month) - 1) / 3) + 1)} ${year ?? ''}`;
    case 'MONTH':
      return formatPeriod(civil.slice(0, 7), locale);
    case 'DAY':
      return datePattern(civil, pattern, locale);
  }
}

function dateTimeLabel(raw: string, pattern: string, timeZone: string, locale: string): string {
  const iso = raw.includes('T') ? raw : `${raw.replace(' ', 'T')}${raw.endsWith('Z') ? '' : 'Z'}`;
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return raw;
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(at);
  const part = (type: string): string => parts.find((p) => p.type === type)?.value ?? '';
  const civil = `${part('year')}-${part('month')}-${part('day')}`;
  const date = datePattern(civil, pattern.split(' ')[0] ?? 'DD.MM.YYYY', locale);
  const time = pattern.endsWith(':ss')
    ? `${part('hour')}:${part('minute')}:${part('second')}`
    : `${part('hour')}:${part('minute')}`;
  return `${date} ${time}`;
}

/**
 * One cell as the viewer shows it. `raw` is the warehouse's text or null. Intl's no-break spaces
 * (`261,90\u00a0€`, `0,0\u202f%`) become ordinary spaces: a report is copied, printed and searched
 * as text, and the cells keep together by `white-space: nowrap`, not by the character.
 */
export function formatReportCell(raw: string | null | undefined, context: CellContext): string {
  return renderCell(raw, context).replace(/[\u00a0\u202f]/g, ' ');
}

function renderCell(raw: string | null | undefined, context: CellContext): string {
  const settings = context.settings ?? DEFAULT_WORKSPACE_FORMATS;
  const f = settings.formats;
  const format = context.format;
  const locale = context.locale ?? settings.locale ?? 'de-DE';
  if (raw === null || raw === undefined || raw === '') return format?.empty ?? f.empty;

  const kind =
    format?.kind ??
    (context.type === 'NUMBER'
      ? 'NUMBER'
      : context.type === 'DATE'
        ? 'DATE'
        : context.type === 'DATETIME'
          ? 'DATETIME'
          : context.type === 'BOOLEAN'
            ? 'BOOLEAN'
            : 'TEXT');

  if (context.bucket !== undefined && (context.type === 'DATE' || context.type === 'DATETIME')) {
    return bucketLabel(civilPart(raw), context.bucket, locale, format?.pattern ?? f.date.pattern);
  }

  switch (kind) {
    case 'TEXT':
      return raw;
    case 'BOOLEAN': {
      // W-5: the workspace labels apply to BOOLEAN only; the lake's 'true'/'false' STRING flags
      // stay text unless the report formats them explicitly.
      if (context.type !== 'BOOLEAN' && format?.kind !== 'BOOLEAN') return raw;
      const truthy = raw === 'true' || raw === 't' || raw === '1';
      return truthy
        ? (format?.trueLabel ?? f.boolean.trueLabel)
        : (format?.falseLabel ?? f.boolean.falseLabel);
    }
    case 'DATE':
      return datePattern(civilPart(raw), format?.pattern ?? f.date.pattern, locale);
    case 'DATETIME':
      return dateTimeLabel(
        raw,
        format?.pattern ?? f.datetime.pattern,
        settings.timeZone ?? 'Europe/Berlin',
        locale,
      );
    case 'NUMBER':
    case 'CURRENCY':
    case 'PERCENT': {
      const value = Number(raw);
      if (!Number.isFinite(value)) return raw;
      if (kind === 'PERCENT') {
        const decimals = format?.decimals ?? f.percent.decimals;
        return new Intl.NumberFormat(locale, {
          style: 'percent',
          minimumFractionDigits: decimals,
          maximumFractionDigits: decimals,
        }).format(value);
      }
      if (kind === 'CURRENCY') {
        const currency = context.rowCurrency ?? format?.currency ?? f.currency.code;
        const decimals = format?.decimals ?? f.currency.decimals;
        if (decimals === 2) return formatCurrency(Math.round(value * 100), currency, locale);
        return new Intl.NumberFormat(locale, {
          style: 'currency',
          currency,
          minimumFractionDigits: decimals,
          maximumFractionDigits: decimals,
        }).format(value);
      }
      // A NUMBER without a format: integers stay integers (a count is "3", not "3,00").
      const decimals =
        format?.decimals ?? (Number.isInteger(value) && !raw.includes('.') ? 0 : f.number.decimals);
      return numberFormat(locale, decimals, format?.grouping ?? f.number.grouping).format(value);
    }
  }
}
